/**
 * Converts a Base64 string to a File object.
 *
 * @param base64 - The Base64 string to convert.
 * @param fileName - The desired name of the file (without extension).
 * @returns A File object created from the Base64 string.
 */
export function base64ToFile(base64: string, fileName: string): File {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  // Determine the file extension from the MIME type
  const extension = mime.split('/')[1]; // e.g., "jpeg" for "image/jpeg"
  const fullFileName = `${fileName}.${extension}`;

  return new File([u8arr], fullFileName, { type: mime });
}

export const supportedMimeTypes: Record<string, string[]> = {
  doc: ['application/msword', 'application/doc', 'application/x-msword'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/x-docx',
  ],
  pdf: [
    'application/pdf',
    'application/x-pdf',
    'application/acrobat',
    'applications/vnd.pdf',
  ],
  xls: [
    'application/vnd.ms-excel',
    'application/msexcel',
    'application/x-excel',
    'application/x-msexcel',
  ],
  xlsx: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-xlsx',
  ],
  ppt: [
    'application/vnd.ms-powerpoint',
    'application/mspowerpoint',
    'application/x-powerpoint',
    'application/x-mspowerpoint',
  ],
  pptx: [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/x-pptx',
  ],
  odt: [
    'application/vnd.oasis.opendocument.text',
    'application/x-vnd.oasis.opendocument.text',
  ],
  jpeg: ['image/jpg', 'image/jpeg'],
  jpg: ['image/jpg', 'image/jpeg'],
  png: ['image/png'],
  webp: ['image/webp'],
};

const mimeCategoryToIcon: Record<string, string> = {
  doc: '/assets/icons/doc-icon.svg',
  docx: '/assets/icons/docx-icon.svg',
  pdf: '/assets/icons/pdf-icon.svg',
  xls: '/assets/icons/xls-icon.svg',
  xlsx: '/assets/icons/xlsx-icon.svg',
  ppt: '/assets/icons/ppt-icon.svg',
  pptx: '/assets/icons/pptx-icon.svg',
  odt: '/assets/icons/odt-icon.svg',
  mp4: '/assets/icons/mp4-icon.svg',
  mov: '/assets/icons/mov-icon.svg',
};

export function isValidFileType(file: File): boolean {
  const fileType = file.type;
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (!extension || !supportedMimeTypes[extension]) {
    console.error(`Unsupported file type: ${extension}`);
    return false;
  }

  if (!supportedMimeTypes[extension].includes(fileType)) {
    return false;
  }

  return true;
}

function getCategoryForMimeType(mimeType: string): string | null {
  for (const category in supportedMimeTypes) {
    if (supportedMimeTypes[category].includes(mimeType)) {
      return category;
    }
  }
  return null;
}

export function getIconForMimeType(mimeType: string): string {
  const category = getCategoryForMimeType(mimeType);
  return category ? mimeCategoryToIcon[category] : '/assets/icons/pdf-icon.svg';
}

export function resizeAndPadImage(
  file: File,
  targetSize: number,
  padding: number = 0
): Promise<string> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate dimensions with padding
        const totalSize = targetSize + padding * 2;
        canvas.width = totalSize;
        canvas.height = totalSize;

        // Fill background
        ctx!.fillStyle = 'transparent';
        ctx!.fillRect(0, 0, totalSize, totalSize);

        // Calculate scale to fit image within target size while maintaining aspect ratio
        const scale = Math.min(targetSize / img.width, targetSize / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;

        // Center the scaled image including padding
        const xOffset = padding + (targetSize - scaledWidth) / 2;
        const yOffset = padding + (targetSize - scaledHeight) / 2;

        // Draw the image centered with padding
        ctx!.drawImage(img, xOffset, yOffset, scaledWidth, scaledHeight);

        resolve(canvas.toDataURL()); // Return base64 image
      };
    };
  });
}
